import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { RevenueCatProvider } from './contexts/RevenueCatContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { TrialProvider } from './contexts/TrialContext';
import { Layout } from './components/Layout';
import { ManifestationCoach } from './components/ManifestationCoach';
import { BookLibrary } from './components/BookLibrary';
import { Login } from './components/Login';
import { Register } from './components/Register';
import { ForgotPassword } from './components/ForgotPassword';
import { VerifyEmail } from './components/VerifyEmail';
import { Profile } from './components/Profile';
import { Settings } from './components/Settings';
import { Charities } from './components/Charities';
import { Commitment } from './components/Commitment';
import { Success } from './components/Success';
import { DanaFloatingSymbol } from './components/DanaFloatingSymbol';
import { SubscriptionModal } from './components/SubscriptionModal';
import { TermsOfService } from './components/TermsOfService';
import { PrivacyPolicy } from './components/PrivacyPolicy';
import { ConsentManager } from './components/ConsentManager';
import { Toaster } from 'react-hot-toast';
import { initializeRevenueCat } from './lib/revenuecat';
import { validateEnvironmentVariables } from './utils/security';
import { useTranslation } from './hooks/useTranslation';
import { PerformanceDashboard } from './components/PerformanceDashboard';

function App() {
  const [isInitialized, setIsInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        // Validate environment variables
        const envValidation = validateEnvironmentVariables();
        if (!envValidation.isValid) {
          const missingVars = envValidation.missing.join(', ');
          const invalidVars = envValidation.invalid.join(', ');
          
          if (import.meta.env.PROD) {
            setInitError(`Application configuration error. Please contact support.`);
            return;
          } else {
            setInitError(`Environment variable issues: ${missingVars ? `Missing: ${missingVars}` : ''} ${invalidVars ? `Invalid: ${invalidVars}` : ''}`);
            return;
          }
        }

        // Initialize RevenueCat
        const revenueCatInitialized = await initializeRevenueCat();
        if (!revenueCatInitialized) {
          // Continue with mock mode
        }

        setIsInitialized(true);
      } catch (error) {
        setInitError('Failed to initialize application. Please refresh the page.');
      }
    };

    initializeApp();
  }, []);

  // Show loading or error state during initialization
  if (!isInitialized) {
    return (
      <LanguageProvider>
        <LoadingScreen initError={initError} />
      </LanguageProvider>
    );
  }

  return (
    <LanguageProvider>
      <AuthProvider>
        <RevenueCatProvider>
          <TrialProvider>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AppContent />
            </Router>
          </TrialProvider>
        </RevenueCatProvider>
      </AuthProvider>
    </LanguageProvider>
  );
}

// Separate component for app content to use hooks that require Router context
const AppContent: React.FC = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [consentCompleted, setConsentCompleted] = useState(false);
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    // Check if we should show subscription modal
    const showPayment = searchParams.get('showPayment');
    if (showPayment === 'true' && user) {
      setShowSubscriptionModal(true);
    }
  }, [searchParams, user]);

  useEffect(() => {
    // Check if consent has been completed
    const termsAccepted = localStorage.getItem('manivo_terms_accepted');
    const cookieConsent = localStorage.getItem('manivo_cookie_consent');
    
    if (termsAccepted && cookieConsent) {
      setConsentCompleted(true);
    }
  }, []);

  // Reset consent state when user logs out
  useEffect(() => {
    if (!user) {
      setConsentCompleted(false);
    }
  }, [user]);

  const handleConsentComplete = () => {
    setConsentCompleted(true);
  };

  return (
    <div className="min-h-screen bg-earth-sand">
      <Layout>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/library" element={<BookLibrary />} />
          <Route path="/login" element={<Login />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/charities" element={<Charities />} />
          <Route path="/commitment" element={<Commitment />} />
          <Route path="/success" element={<Success />} />
        </Routes>
      </Layout>
      
      {/* Dana Floating Symbol - appears on all pages */}
      <DanaFloatingSymbol />

      {/* Show consent manager for first-time visitors */}
      {!consentCompleted && (
        <ConsentManager 
          key={user ? user.id : 'no-user'} 
          onConsentComplete={handleConsentComplete} 
        />
      )}
      
      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <SubscriptionModal 
          isOpen={showSubscriptionModal}
          onClose={() => setShowSubscriptionModal(false)}
        />
      )}

      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#363636',
            color: '#fff',
          },
        }}
      />
      
      {/* Performance Dashboard - Development Only */}
      <PerformanceDashboard />
    </div>
  );
};

// Loading screen component that can use translations
const LoadingScreen: React.FC<{ initError: string | null }> = ({ initError }) => {
  const { t, isLoading } = useTranslation();

  return (
    <div className="min-h-screen bg-earth-sand flex items-center justify-center">
      <div className="text-center">
        {initError ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <div className="text-red-600 font-semibold mb-2">
              {isLoading ? 'Initialization Error' : t('app.initError')}
            </div>
            <p className="text-red-700 text-sm">{initError}</p>
            {!import.meta.env.PROD && (
              <button 
                onClick={() => window.location.reload()} 
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                {isLoading ? 'Retry' : t('app.retryButton')}
              </button>
            )}
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-earth-bronze mb-4"></div>
            <p className="text-gray-600">
              {isLoading ? 'Initializing Manivo...' : t('app.loading')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// HomePage component
const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [consentCompleted, setConsentCompleted] = useState(false);

  useEffect(() => {
    // Check if we should show subscription modal
    const showPayment = searchParams.get('showPayment');
    if (showPayment === 'true' && user) {
      setShowSubscriptionModal(true);
    }
  }, [searchParams, user]);

  useEffect(() => {
    // Check if consent has been completed
    const termsAccepted = localStorage.getItem('manivo_terms_accepted');
    const cookieConsent = localStorage.getItem('manivo_cookie_consent');
    
    if (termsAccepted && cookieConsent) {
      setConsentCompleted(true);
    }
  }, []);

  const handleConsentComplete = () => {
    setConsentCompleted(true);
  };

  return (
    <>
      {/* Hero Section */}
      <div className="bg-transparent pt-12 pb-8">
        <div className="text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-earth-stone mb-6 sm:mb-8 leading-tight tracking-tight">
            {t('app.heroTitle')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-earth-bronze via-earth-terracotta to-earth-bronze bg-[length:200%_auto] animate-gradient">
              {t('app.name')}
            </span>
          </h1>
          <p className="text-xl sm:text-2xl text-gray-700 mb-8 sm:mb-12 leading-relaxed max-w-3xl mx-auto font-medium">
            {t('app.tagline')}
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-transparent pb-16">
        <div>
          <ManifestationCoach />
        </div>
      </div>
    </>
  );
};

export default App;